import { LocationService } from '@/app/services/location.service';
import { MAX_LOCATION_SUGGESTIONS, MIN_SEARCH_TERM_LENGTH, SEARCH_DEBOUNCE_TIME } from '@/app/shared/constants/search.constants';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, debounceTime, distinctUntilChanged, finalize, fromEvent, of, switchMap, tap } from 'rxjs';
import { UserLocation } from '../../../../../models/location.model';
import { WeatherStore } from '../../services/weather.store';
import { SearchItem } from './search-results.model';

@Component({
  selector: 'dr-flex-home-ui-search-results',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './search-results.component.html',
  styleUrls: ['./search-results.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SearchResultsComponent implements AfterViewInit {
  readonly optionChosen =  output<UserLocation>();
  
  @ViewChild('inputRef', { static: true }) inputRef!: ElementRef<HTMLInputElement>;
  private store = inject(WeatherStore);
  private destroyRef = inject(DestroyRef);
  private locationService = inject(LocationService);
  private host = inject(ElementRef<HTMLElement>);

  ctrl = new FormControl<string>(this.store.location()?.name ?? '', { nonNullable: true });

  loading = signal(false);
  open = signal(false);
  results = signal<SearchItem[]>([]);
  activeIndex = signal<number>(-1);

  hasValue = computed(() => (this.ctrl.value?.trim().length ?? 0) > 0);
  showNoResults = computed(() => !this.loading() && this.open() && (this.ctrl.value?.trim().length ?? 0) >= MIN_SEARCH_TERM_LENGTH && this.results().length === 0);
  constructor() {
    effect(() => {
      const location = this.store.location();
      this.ctrl.setValue(location?.name ?? '', { emitEvent: false });
    });
    this.ctrl.valueChanges
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_TIME),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
        switchMap((term) => {
          const q = (term ?? '').trim();

          if (q.length < MIN_SEARCH_TERM_LENGTH) {
            this.loading.set(false);
            this.open.set(false);
            this.results.set([]);
            this.activeIndex.set(-1);
            return of<SearchItem[]>([]);
          }

          this.loading.set(true);
          // search for results from API
          return this.locationService.getLocationSuggestions(q).pipe(
            tap((res) => {
              const list = res.slice(0, MAX_LOCATION_SUGGESTIONS);
              this.results.set(list);
              this.activeIndex.set(-1);
            }),
            catchError(() => {
              this.results.set([]);
              return of<SearchItem[]>([]);
            }),
            finalize(() => {
              this.loading.set(false);
              const current = (this.ctrl.value ?? '').trim();
              this.open.set(current.length >= MIN_SEARCH_TERM_LENGTH);
            })
          );
        })
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    // Keydown event
    fromEvent<KeyboardEvent>(this.inputRef.nativeElement, 'keydown')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ev) => this.onKeydown(ev));

    // Focus event
    fromEvent<FocusEvent>(this.inputRef.nativeElement, 'focus')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onFocus());
  }

  // close dropdown when clicking outside
  @HostListener('document:click', ['$event'])
  onDocClick(ev: MouseEvent) {
    if (!this.host.nativeElement.contains(ev.target as Node)) {
      this.open.set(false);
      this.activeIndex.set(-1);
    }
  }

  onFocus() {
    if ((this.ctrl.value?.trim().length ?? 0) >= 3 && this.results().length > 0) {
      this.open.set(true);
    }
  }

  onClear() {
    this.ctrl.setValue('');
    this.results.set([]);
    this.activeIndex.set(-1);
    this.open.set(false);
    this.inputRef.nativeElement.focus();
  }

  // Keyboard navigation & commit
  private onKeydown(e: KeyboardEvent) {
    const list = this.results();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!this.open()) this.open.set(true);
      const next = Math.min(this.activeIndex() + 1, list.length - 1);
      this.activeIndex.set(next);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = Math.max(this.activeIndex() - 1, 0);
      this.activeIndex.set(prev);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!this.open()) return;
      const idx = this.activeIndex();
      if (idx >= 0 && list[idx]) {
        this.commit(list[idx]);
      } else if (list.length > 0) {
        this.commit(list[0]);
      }
      return;
    }
    if (e.key === 'Escape') {
      this.open.set(false);
      this.activeIndex.set(-1);
      return;
    }
  }

  onHover(i: number) {
    this.activeIndex.set(i);
  }

  select(item: SearchItem) {
    this.commit(item);
  }

  private commit(item: SearchItem) {
    this.ctrl.setValue(item.label, { emitEvent: false });
    this.open.set(false);
    this.results.set([]);
    this.activeIndex.set(-1);
    if (item.location) {
      this.optionChosen.emit(item.location);
    }
  }
}
